import { Box, Button, Card, Chip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import {
  Datagrid,
  DateInput,
  FilterForm,
  FunctionField,
  List,
  Pagination,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
  useListContext,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { EmptyContent } from 'src/components/empty-content';
import { SplashScreen } from 'src/components/loading-screen';
import TxField from 'src/pages/admin/components/TxField';
import { DashboardContent } from 'src/layouts/dashboard';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { resetPredictionTradingCursorStore } from './dataProvider';
import { Resources } from '../resource';
import { useMemo } from 'react';

const formatNumber = (value?: string | number | null) => {
  if (value == null) return '-';

  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: 6,
  });
};

const parseFilterDate = (value: string, boundary: 'start' | 'end') => {
  if (!value) {
    return null;
  }

  const parsedDate = dayjs(value);

  if (!parsedDate.isValid()) {
    return null;
  }

  return (boundary === 'start' ? parsedDate.startOf('day') : parsedDate.endOf('day')).valueOf();
};

const formatFilterDate = (value: string | number | null | undefined) => {
  if (value == null || value === '') {
    return '';
  }

  const parsedDate = dayjs(value);

  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : '';
};

const ListLoadingOverlay = () => {
  const { isLoading } = useListContext();

  if (!isLoading) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 10,
        bgcolor: 'rgba(255,255,255,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SplashScreen />
    </Box>
  );
};

const PredictionTradingListActions = () => {
  const { filterValues, setFilters, setPage } = useListContext();
  const translate = useTranslate();
  const hasActiveFilters = Object.keys(filterValues ?? {}).length > 0;

  const SIDE_CHOICES = useMemo(
    () => [
      { id: 'BUY', name: translate('module.predictionTrading.sideValues.BUY', { _: 'Buy' }) },
      { id: 'SELL', name: translate('module.predictionTrading.sideValues.SELL', { _: 'Sell' }) },
    ],
    [translate]
  );

  const STATUS_CHOICES = useMemo(
    () => [
      {
        id: 'TRADE_STATUS_CONFIRMED',
        name: translate('module.predictionTrading.tradeStatus.CONFIRMED', { _: 'Confirmed' }),
      },
      {
        id: 'TRADE_STATUS_FAILED',
        name: translate('module.predictionTrading.tradeStatus.FAILED', { _: 'Failed' }),
      },
      {
        id: 'TRADE_STATUS_MATCHED',
        name: translate('module.predictionTrading.tradeStatus.MATCHED', { _: 'Matched' }),
      },
      {
        id: 'TRADE_STATUS_MINED',
        name: translate('module.predictionTrading.tradeStatus.MINED', { _: 'Mined' }),
      },
    ],
    [translate]
  );

  const handleResetFilters = () => {
    resetPredictionTradingCursorStore();
    setPage(1);
    setFilters({});
  };

  return (
    <TopToolbar
      sx={{
        minHeight: 90,
        alignItems: 'center',
        px: 3,
        pt: 1,
        overflow: 'visible',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          gap: 2,
          flexWrap: 'wrap',
          '& form': {
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            flex: 1,
          },
          '& .ra-input': { mt: 0, mb: 0 },
          '& .MuiFormControl-root': { mt: 0, mb: 0 },
          '& .MuiTextField-root': { mt: 0, mb: 0 },
          '& .MuiInputBase-root': { minHeight: 40 },
        }}
      >
        <FilterForm
          filters={[
            <TextInput
              key="maker"
              source="maker"
              label="module.predictionTrading.maker"
              alwaysOn
              resettable
              size="small"
              sx={{ minWidth: 220 }}
            />,
            <SelectInput
              key="side"
              source="side"
              label="module.predictionTrading.side"
              choices={SIDE_CHOICES}
              alwaysOn
              resettable
              emptyText={translate('actions.all')}
              size="small"
              sx={{ minWidth: 100 }}
            />,
            <SelectInput
              key="status"
              source="status"
              label="module.predictionTrading.status"
              choices={STATUS_CHOICES}
              alwaysOn
              resettable
              emptyText={translate('actions.all')}
              size="small"
              sx={{ minWidth: 160 }}
            />,
            <DateInput
              key="from"
              source="from"
              label="common.fromTime"
              alwaysOn
              size="small"
              parse={(value: string) => parseFilterDate(value, 'start')}
              format={formatFilterDate}
              sx={{ minWidth: 180 }}
            />,
            <DateInput
              key="to"
              source="to"
              label="common.toTime"
              alwaysOn
              size="small"
              parse={(value: string) => parseFilterDate(value, 'end')}
              format={formatFilterDate}
              sx={{ minWidth: 180 }}
            />,
          ]}
        />
        <Button
          variant="outlined"
          size="medium"
          onClick={handleResetFilters}
          disabled={!hasActiveFilters}
        >
          {translate('common.reset')}
        </Button>
      </Box>
    </TopToolbar>
  );
};

const PredictionTradingPagination = () => {
  const { page, setPage } = useListContext();
  const refresh = useRefresh();
  const translate = useTranslate();

  const handleBackToFirstPage = () => {
    resetPredictionTradingCursorStore();
    setPage(1);
    refresh();
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} px={2} py={1}>
      <Button variant="outlined" size="small" onClick={handleBackToFirstPage}>
        {translate('common.refresh', { _: 'Refresh' })}
      </Button>
      <Pagination rowsPerPageOptions={[10, 20, 50]} />
    </Box>
  );
};

const PredictionTradingList = () => {
  const translate = useTranslate();

  const sideColor = (side?: string) => {
    const value = String(side || '').toUpperCase();
    if (value === 'BUY' || value === 'LONG' || value === 'YES') return 'success' as const;
    if (value === 'SELL' || value === 'SHORT' || value === 'NO') return 'error' as const;
    return 'default' as const;
  };

  const formatStatusLabel = (status?: string) => {
    if (!status) return '-';

    const normalizedStatus = String(status)
      .toUpperCase()
      .replace(/^TRADE_STATUS_/, '');

    return translate(`module.predictionTrading.tradeStatus.${normalizedStatus}`, { _: status });
  };

  return (
    <DashboardContent>
      <PermissionGuard
        resource="prediction-builder-trade-stats"
        action="read"
        fallback={<ErrorPage403 />}
      >
        <CustomBreadcrumbs
          heading={translate('resource.predictionTrading', { _: 'Prediction Trading' })}
          links={[
            {
              name: translate('resource.predictionTrading', { _: 'Prediction Trading' }),
              href: `/${Resources.predictionTrading}`,
            },
            { name: translate('resource.list', { _: 'List' }) },
          ]}
        />

        <Card
          sx={{
            mt: 3,
            pb: 2,
            '.col-side-center': { textAlign: 'center' },
            '.col-status-center': { textAlign: 'center' },
          }}
        >
          <List
            actions={<PredictionTradingListActions />}
            empty={false}
            perPage={10}
            exporter={false}
            pagination={<PredictionTradingPagination />}
            sort={{ field: 'matchTime', order: 'DESC' }}
          >
            <Box sx={{ overflow: 'auto', position: 'relative' }}>
              <ListLoadingOverlay />

              <Datagrid
                rowClick={false}
                empty={<EmptyContent />}
                bulkActionButtons={false}
                sx={{
                  '& .RaDatagrid-tableWrapper': { overflowX: 'auto' },
                  '& .RaDatagrid-table': { tableLayout: 'auto' },
                  '& .column-market': { minWidth: 140, whiteSpace: 'nowrap' },
                  '& .column-maker': { minWidth: 140, whiteSpace: 'nowrap' },
                  '& .column-outcome': { minWidth: 120, whiteSpace: 'nowrap' },
                  '& .column-size': { minWidth: 120, whiteSpace: 'nowrap' },
                  '& .column-sizeUsdc': { minWidth: 120, whiteSpace: 'nowrap' },
                  '& .column-price': { minWidth: 120, whiteSpace: 'nowrap' },
                  '& .column-feeUsdc': { minWidth: 120, whiteSpace: 'nowrap' },
                  '& .column-builderFee': { minWidth: 120, whiteSpace: 'nowrap' },
                  '& .column-transactionHash': { minWidth: 140, whiteSpace: 'nowrap' },
                  '& .column-matchTime': { minWidth: 180, whiteSpace: 'nowrap' },
                }}
              >
                <TextField
                  source="maker"
                  label="module.predictionTrading.maker"
                  sortable={false}
                  headerClassName="column-maker"
                  cellClassName="column-maker"
                />

                <FunctionField
                  source="side"
                  label="module.predictionTrading.side"
                  sortable={false}
                  headerClassName="col-side-center"
                  cellClassName="col-side-center"
                  render={(record: any) => {
                    const side = record?.side;
                    const sideLabel = side
                      ? translate(`module.predictionTrading.sideValues.${side}`, { _: side })
                      : '-';
                    return (
                      <Chip
                        label={sideLabel}
                        color={sideColor(side)}
                        size="small"
                        sx={{ mx: 'auto' }}
                      />
                    );
                  }}
                />

                <TextField
                  source="outcome"
                  label="module.predictionTrading.outcome"
                  sortable={false}
                  headerClassName="column-outcome"
                  cellClassName="column-outcome"
                />

                <FunctionField
                  source="size"
                  label="module.predictionTrading.size"
                  sortable={false}
                  headerClassName="column-size"
                  cellClassName="column-size"
                  render={(record: any) => formatNumber(record?.size)}
                />

                <FunctionField
                  source="price"
                  label="module.predictionTrading.price"
                  sortable={false}
                  headerClassName="column-price"
                  cellClassName="column-price"
                  render={(record: any) => formatNumber(record?.price)}
                />

                <FunctionField
                  source="sizeUsdc"
                  label="module.predictionTrading.volume"
                  sortable={false}
                  headerClassName="column-sizeUsdc"
                  cellClassName="column-sizeUsdc"
                  render={(record: any) => {
                    const formattedAmount = formatNumber(record?.sizeUsdc);

                    return formattedAmount === '-' ? formattedAmount : `$${formattedAmount}`;
                  }}
                />

                <FunctionField
                  source="feeUsdc"
                  label="module.predictionTrading.feeUsdc"
                  sortable={false}
                  headerClassName="column-feeUsdc"
                  cellClassName="column-feeUsdc"
                  render={(record: any) => `$${formatNumber(record?.feeUsdc)}`}
                />

                <FunctionField
                  source="builderFee"
                  label="module.predictionTrading.builderFee"
                  sortable={false}
                  headerClassName="column-builderFee"
                  cellClassName="column-builderFee"
                  render={(record: any) => `$${formatNumber(record?.builderFee)}`}
                />

                <FunctionField
                  source="status"
                  label="module.predictionTrading.status"
                  sortable={false}
                  headerClassName="col-status-center"
                  cellClassName="col-status-center"
                  render={(record: any) => (
                    <Chip
                      label={formatStatusLabel(record?.status)}
                      color="default"
                      size="small"
                      sx={{ mx: 'auto' }}
                    />
                  )}
                />

                <FunctionField
                  source="matchTime"
                  label="module.predictionTrading.matchTime"
                  sortable={false}
                  headerClassName="column-matchTime"
                  cellClassName="column-matchTime"
                  render={(record: any) => (
                    <Typography variant="body2">
                      {record?.matchTime
                        ? dayjs(record.matchTime).format('YYYY-MM-DD HH:mm:ss')
                        : '-'}
                    </Typography>
                  )}
                />

                <TextField
                  source="market"
                  label="module.predictionTrading.market"
                  sortable={false}
                  headerClassName="column-market"
                  cellClassName="column-market"
                />

                <FunctionField
                  label="module.predictionTrading.transactionHash"
                  sortable={false}
                  headerClassName="column-transactionHash"
                  cellClassName="column-transactionHash"
                  render={(record: any) => {
                    const tx = record?.transactionHash;
                    if (!tx) return '-';
                    return <TxField id={tx} width={120} />;
                  }}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default PredictionTradingList;
